import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from './base.entity';

/**
 * Storefront-only overlay for category strings. The customer-facing
 * category for an item lives in `item.category` (a denormalized text
 * column populated by the catalog ingest pipeline) — there is NO
 * corresponding `category` table row for these strings, so we can't
 * hang storefront fields off of `category`.
 *
 * Instead, this thin overlay table is keyed by the category name
 * string itself. The Categories admin page in CRM enumerates distinct
 * `item.category` strings + LEFT JOINs this table to surface current
 * settings; missing rows mean "no override / no image".
 *
 * Scope: tenant-isolated (DB-per-tenant) — no tenant column needed.
 *
 * Why a separate table instead of mutating `category`:
 *   1. The `category` table holds internal supplier hierarchy data
 *      (e.g. "TNG - All products", "GIL - Tier3") that is unrelated
 *      to customer categorization. Polluting it with storefront
 *      fields would conflate two domains.
 *   2. The customer category list comes from `item.category`
 *      strings, which have no `category._id` to FK to. A
 *      string-keyed overlay is the natural shape.
 *
 * Trade-off: a category rename in the source data orphans the
 * override (we can't follow the rename without a stable id). Renames
 * are rare and the failure mode is "image goes missing, not broken
 * page" — acceptable for an MVP.
 */
@Index('csset_categoryName_uindex', ['categoryName'], { unique: true })
@Entity('category_storefront_settings')
export class CategoryStorefrontSettingsEntity extends BaseEntity {
	/**
	 * Exact-match key against `item.category`. Preserved as-is from
	 * the source data so admin overrides survive ingest.
	 */
	@Column('varchar', { name: 'categoryName', length: 255 })
	public categoryName!: string;

	/**
	 * Optional admin-friendly label override shown to customers on
	 * the storefront. Falls back to `categoryName` when null.
	 */
	@Column('varchar', { name: 'displayName', nullable: true, length: 255 })
	public displayName!: string | null;

	/**
	 * Public CDN URL for the homepage category tile image. Uploaded
	 * via `POST /api/categories/upload-image` (public CDN — same
	 * flow as promo banner art).
	 */
	@Column('varchar', { name: 'imageUrl', nullable: true, length: 2048 })
	public imageUrl!: string | null;
}
